import React, {useContext, useState, useEffect} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from 'layout';
import classes from './OnboardingPage.module.scss'
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from 'react-bootstrap';
import { TheSentence, TheIntroduction, SkillsExpertiseInterests, Portfolio, CommunitySelection } from 'components';
import { getUserProfile, processUserProfileData, updateUserMetadata, updateUserProfile, getTitleAndGoal } from 'lib/user';
import { convertToMarkdown } from 'lib/utils';
import AuthContext from 'app/AuthContext';
import { Alert } from 'react-bootstrap';

const OnboardingPage = () => {
  const { userId } = useParams();
  const [ profile, setProfile ] = useState({});
  const {authToken, forceRefresh, logout, userMetadata} = useContext(AuthContext);
  const [loading, setLoading] = useState(false);  
  const [currentStep, setCurrentStep] = useState(0);
  const [ displayedError, setDisplayedError ] = useState('');
  const navigate = useNavigate();

  /**
     * The function `loadUserProfile` is an asynchronous function that loads a user's profile and updates
     * the state with the profile data.
     * @param userId - The `userId` parameter is the unique identifier of the user whose profile we want
     * to load.
     */
  async function loadUserProfile(userId) {
    setLoading(true);
    const { isSuccess: getProfileSuccess, resp: getProfileResp, err: getProfileErr } =
      await getUserProfile(userId, [{ "key": "details", "value": "true" }, { "key": "tags", "value": "true" }], authToken);

    if (getProfileSuccess) {
      if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("Profile Search || Success", getProfileResp); }
      // Process/format the user profile data
      getProfileResp.results = processUserProfileData(getProfileResp.results);
      setProfile(getProfileResp.results);
      updateUserMetadata(getProfileResp.results.user_avatar, "user_avatar");
      forceRefresh();
    } else {
      if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.error("Profile Search || Error", getProfileResp, getProfileErr); }
      if (getProfileResp) {
        // Log out user if the token session is invalid (most likely expired).
        if (getProfileResp.status_code === "UNAUTHORIZED") {
          logout();
        } else {
          setDisplayedError("There was an error retrieving your profile. Please try again later.");
        }
      } else {
        setDisplayedError("There was a fatal error retrieving your profile. Please try again later.");
      }
    }
    setLoading(false);
  }

  // Trigger loadUserProfile when the component mounts
  useEffect(() => {
    loadUserProfile(userId);
  }, [userId]);

  const steps = [
    <TheSentence
      key="sentence"
      firstName={profile.f_name || ''}
      lastName={profile.l_name || ''}
      location={profile.location || ''}
      tags={profile.tags || ''} />,
    <CommunitySelection/>,
    <SkillsExpertiseInterests key="skillsexpertiseinterests"
      tags={profile.tags || ''} />,
    <TheIntroduction key="introduction"
      intro={profile.intro_summary || ''}
      avatar={profile.user_avatar || ''} />,
    <Portfolio key="portfolio"
      experience={profile.experience || ''}
      portfolios={profile.portfolios || []} />
  ];

  const handleNext = () => {
    if (currentStep === 0) {
        const profile = sessionStorage.getItem('profile');
        const parsedProfile = JSON.parse(profile);
        const { title: getTitle, goal: getGoal } = getTitleAndGoal(parsedProfile.tags);
        if (parsedProfile.fname && parsedProfile.lname && parsedProfile.location && getTitle && getGoal) {
          setCurrentStep((prevStep) => (prevStep + 1) % steps.length);
          setDisplayedError(null);
        }
        else {
          setDisplayedError("Please fill out all fields before moving on.");
        }
    }
    else {
      setCurrentStep((prevStep) => (prevStep + 1) % steps.length);
    }
  };

  const handlePrev = () => {
    setCurrentStep((prevStep) => (prevStep - 1 + steps.length) % steps.length);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  /**
     * The function `changeUserProfile` updates a user's profile information and redirects them to
     * their profile page if the update is successful.
     * @param userId - The userId parameter is the unique identifier of the user whose profile is being
     * updated. It is used to identify the user in the updateUserProfile function.
     * @param data - The `data` parameter is an object that contains the user's profile data. It can
     * include properties such as `profileIntro`, `experience`, and `education`. These properties are
     * expected to be in a specific format, so the code is using the `convertToMarkdown` function to
     * convert them to
     */
  async function changeUserProfile(userId, data) {
    // Formatting covers lists and links
    if (data.intro) { data.intro = convertToMarkdown(data.intro); }
    if (data.experience) { data.experience = convertToMarkdown(data.experience); }
    const defErrorMsg = "There was a fatal error updating your profile. Please try again later.";

    try {
        const { isSuccess: updateUserSuccess, resp: updateUserResp, err: updateUserErr } = await updateUserProfile(userId, data, authToken);

        if (updateUserSuccess) {
            if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("User Profile Update || Success:", updateUserResp); }
            navigate(`/discovery`);
        } else {
            if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.log("User Profile Update || Error:", updateUserResp, updateUserErr); }
            if (updateUserResp) {
                // Log out user if the token session is invalid (most likely expired).
                if (updateUserResp.status_code === "UNAUTHORIZED") { 
                    logout();
                } else {
                    setDisplayedError(defErrorMsg);
                    throw new Error();
                }
            } else {
                setDisplayedError(defErrorMsg);
                throw new Error();
            }
        }
    } catch (error) {
        if (process.env.REACT_APP_ENVIRONMENT === "dev") { console.error("Error updating user's profile:", error); }
        throw error;
    }
}

  const handleSubmit = async (event) => {
    if (userId === userMetadata.id) {
      event.preventDefault();
      const updatedProfile = JSON.parse(sessionStorage.getItem('profile'));
      const [city, province, country] = updatedProfile.location.split(", ").map((part) => part.trim());

      const parsedCommunities = updatedProfile.communities && updatedProfile.communities.map(community => {
        return {id: community.id, value: community.value, icon: community.icon};
      });

      const profileData = {
        first_name: updatedProfile.fname,
        last_name: updatedProfile.lname,
        city,
        province,
        country,
        intro_summary: updatedProfile.intro,
        experience: updatedProfile.experience,
        portfolios: updatedProfile.portfolios,
        tags: updatedProfile.tags,
        communities: parsedCommunities,
        events: updatedProfile.events || null
      };

      try {
        await changeUserProfile(userId, profileData);
        sessionStorage.removeItem('profile');
      } catch (error) {
        console.error("Error updating profile:", error);
      }
    } else {
      setDisplayedError("You are not authorized to update this profile.");
    }
  };

  return (
    <Layout tabHeader="Onboarding Flow">
      <div className={classes.onboardingFlowContainer}>
        <AnimatePresence exitBeforeEnter>
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className={classes.onboardingContent}
          >
            {steps[currentStep]}
          </motion.div>
        </AnimatePresence>
        <div className={classes.naviAndProgress}>
          <div className={classes.navigationButtons}>
            {currentStep !== 0 && (
              <Button className={classes.navButton} onClick={handlePrev}>
                Back
              </Button>
            )}
            {currentStep >= 1 && currentStep < steps.length - 1 && (
              <Button className={classes.skipButton} onClick={handleSubmit}>
                Save & finish later
              </Button>
            )}
            {currentStep !== steps.length - 1 && (
              <Button className={classes.navButton} onClick={handleNext}>
                Next
              </Button>
            )}
            {currentStep === steps.length - 1 && (
              <Button className={classes.navButton} onClick={handleSubmit}>
                Submit
              </Button>
            )}
          </div>
          <div className={classes.progress}>{currentStep+1}/{steps.length}</div>
          <div className='sentence-berror'>
            <Alert variant="danger" className="animated-fast fadeInDownMenu"><Alert.Heading style={{ fontSize: "13px", color: "red" }}>{displayedError}</Alert.Heading></Alert>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OnboardingPage;